import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Bug, List, Kanban, BarChart2, Plus, Clock, AlertTriangle, Paperclip, X, Image as ImageIcon, Rocket, CheckCircle, Download, Award, Zap, Tags, PenTool, Trash2, Save } from 'lucide-react';
import TaskBoard from './TaskBoard';
import { DEFAULT_STATUSES, DEFAULT_STATUS_META, fmtDate, initials, AVATAR_COLORS, ACCENT_LIGHT } from '../App';

const Field = ({label,children,T})=><div><div style={{fontSize:10,color:T.textFaint,fontWeight:700,letterSpacing:"0.12em",marginBottom:8}}>{label}</div>{children}</div>;

export default function BetaPortal({ ctx }) {
  const {users, filterAssignee, setFilterAssignee, getTaskAssigneeIds, T, iS, nl2Betas, projects, allTasks, currentUser, isAdmin, notify, api, apiFetch, token, lang, activeProjectId, setActiveProjectId } = ctx;
  
  const isEN = lang === "en";

  const [activeTab, setActiveTab] = useState("bugs");
  const [viewMode, setViewMode] = useState("board");
  const fileInputRef = useRef(null);

  const [showBugModal, setShowBugModal] = useState(false);
  const [issubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const [filterStatus, setFilterStatus] = useState("Alle");
  const [filterTag, setFilterTag] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const [editingBeta, setEditingBeta] = useState(null);
  const [editBetaLinks, setEditBetaLinks] = useState([]);
  const [isSavingBeta, setIsSavingBeta] = useState(false);

  const betaProject = (projects || []).find(p => p?.name === "Beta-Reports");
  const latestBeta = (nl2Betas || [])?.length > 0 ? nl2Betas[0].version : "";

  useEffect(() => {
    if (betaProject && activeProjectId !== betaProject.id && setActiveProjectId) {
      setActiveProjectId(betaProject.id);
    }
  }, [betaProject, activeProjectId, setActiveProjectId]);

  const [bugForm, setBugForm] = useState({
    title: "", description: "", steps: "", severity: "Normal", version: latestBeta, tagIds: []
  });

  const betaTasks = useMemo(() => {
    if (!betaProject || !allTasks) return [];
    return allTasks.filter(t => (t.project_id === betaProject.id || t.projectId === betaProject.id) && !t.archived);
  }, [allTasks, betaProject]);

  const filteredBetaTasks = useMemo(() => {
    return betaTasks.filter(t => {
      if (filterStatus !== "Alle" && t.status !== filterStatus) return false;
      if (filterTag && !(t.tags || []).some(tg => String(tg.id) === String(filterTag))) return false;
      if (searchQuery && !t.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      
      // 🚀 FIX: Eigenständiger, crash-freier Mitarbeiter-Filter (ohne externe getTaskAssigneeIds Funktion)
      if (filterAssignee && filterAssignee !== "Alle") {
        const assigneeId = parseInt(filterAssignee, 10);
        // Zieht sich sicher die User-IDs aus der Aufgabe
        const taskAssigns = (t.assignees || []).map(a => typeof a === 'object' ? (a.user_id || a.id) : a);
        if (!taskAssigns.includes(assigneeId)) return false;
      }
      
      return true;
    });
  }, [betaTasks, filterStatus, filterTag, searchQuery, filterAssignee]);

  const tasksByStatus = useMemo(() => {
    if (!betaProject) return {};
    const statuses = betaProject.customStatuses?.map(s => s.label) || DEFAULT_STATUSES;
    const map = {};
    statuses.forEach(s => map[s] = []);
    filteredBetaTasks.forEach(t => {
      const st = t.status;
      if (map[st]) map[st].push(t);
      else if (statuses.length > 0) map[statuses[0]].push(t);
    });
    return map;
  }, [filteredBetaTasks, betaProject]);

  const topReporters = useMemo(() => {
    const counts = {};
    betaTasks.forEach(t => {
      const name = t.creator_name || (isEN ? "Unknown" : "Unbekannt");
      counts[name] = (counts[name] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [betaTasks, isEN]);

  const tagStats = useMemo(() => {
    const counts = {};
    const colors = {};
    betaTasks.forEach(t => {
      (t.tags || []).forEach(tg => {
        if (!tg.name.startsWith("V")) { 
           counts[tg.name] = (counts[tg.name] || 0) + 1;
           colors[tg.name] = tg.color;
        }
      });
    });
    return Object.entries(counts)
      .sort((a,b) => b[1] - a[1])
      .map(([name, count]) => ({ name, count, color: colors[name] }));
  }, [betaTasks]);

  if (!betaProject) {
    return <div style={{ padding: 40, textAlign: "center", color: T.textFaint }}><Bug size={48} style={{ opacity: 0.5, marginBottom: 12, margin: "0 auto" }} /><div>{isEN ? "Project 'Beta-Reports' not found." : "Projekt 'Beta-Reports' nicht gefunden."}</div></div>;
  }

  if (projects.length > 0 && allTasks.length === 0 && activeTab === "bugs") {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 30, height: 30, border: `3px solid ${T.border3}`, borderTop: `3px solid ${ACCENT_LIGHT}`, borderRadius: "50%", margin: "0 auto 12px", animation: "spin 0.8s linear infinite" }} />
          <div style={{ color: T.textMuted, fontSize: 13 }}>{isEN ? "Loading bugs..." : "Bugs werden geladen..."}</div>
        </div>
      </div>
    );
  }
  
  const isBetaAdmin = isAdmin || betaProject.created_by === currentUser?.id;
  const canEdit = isAdmin || betaProject.created_by === currentUser?.id || (ctx.getProjectPerm ? ctx.getProjectPerm(betaProject.id) === "edit" : false);

  const portalCtx = {
    ...ctx,
    activeProject: betaProject,
    activeProjectId: betaProject.id,
    projectStatuses: betaProject.customStatuses?.map(s => s.label) || DEFAULT_STATUSES,
    getStatusMeta: (label) => {
      const s = betaProject.customStatuses?.find(s => s.label === label);
      if (s) return { color: s.color, dot: s.color, bg: s.color + "18" };
      return DEFAULT_STATUS_META[label] || { color: "#94A3B8", dot: "#94A3B8", bg: "#94A3B818" };
    },
    canView: true,
    canEdit: canEdit,
    updateTaskStatus: async (taskId, status) => {
      try {
        const task = allTasks.find(t => t.id === taskId);
        if (!task) return;
        await api(`/tasks/${taskId}`, {
          method: "PUT",
          body: JSON.stringify({
            title: task.title,
            description: task.description || "",
            status: status,
            priority: task.priority,
            assignees: (task.assignees || []).map(a => a.user_id || a),
            checklist: task.checklist || [],
            tagIds: (task.tags || []).map(tg => tg.id),
            milestone_id: task.milestone_id,
            depends_on: task.dependencies || []
          })
        });
        if (ctx.loadAllTasks) ctx.loadAllTasks(); 
      } catch (e) {
        notify("Fehler: " + e.message);
      }
    }
  };

  const submitBug = async () => {
    if (!bugForm.title.trim()) return notify(isEN ? "Error: Title missing!" : "Fehler: Titel fehlt!");

    const titleLower = bugForm.title.toLowerCase();
    const isDuplicate = betaTasks.some(t => t.title.toLowerCase().includes(titleLower) || titleLower.includes(t.title.toLowerCase()));

    if (isDuplicate) {
      if (!window.confirm(isEN ? "Warning: A bug with a similar title already exists! Do you really want to submit it anyway?" : "Achtung: Ein Bug mit einem ähnlichen Titel existiert bereits! Willst du ihn wirklich nochmal eintragen?")) {
        return;
      }
    }

    setIsSubmitting(true);
    let prio = 1; 
    if (bugForm.severity === "Kritisch") prio = 0;
    if (bugForm.severity === "Kosmetisch") prio = 3;

    let finalTagIds = [...bugForm.tagIds];
    if (bugForm.version && bugForm.version !== "Unbekannt") {
      const vTag = betaProject.tags?.find(t => t.name.includes(bugForm.version));
      if (vTag && !finalTagIds.includes(vTag.id)) finalTagIds.push(vTag.id);
    }

    const reproductionText = isEN ? "**Reproduction:**" : "**Reproduktion:**";
    const fullDesc = `${bugForm.description}\n\n${reproductionText}\n${bugForm.steps}`.trim();

    try {
      const newTask = await api(`/projects/${betaProject.id}/tasks`, {
        method: "POST",
        body: JSON.stringify({
          title: bugForm.title, description: fullDesc, status: betaProject.customStatuses?.[0]?.label || "Offen",
          priority: prio, assignees: [], checklist: [], tagIds: finalTagIds, milestone_id: null, depends_on: []
        })
      });

      if (selectedFile && newTask.id) {
        const fd = new FormData();
        fd.append("file", selectedFile);
        await apiFetch(`/tasks/${newTask.id}/files`, { method: "POST", body: fd }, token);
      }
      
      notify(isEN ? "Bug successfully reported! 🐞✅" : "Bug erfolgreich eingetragen! 🐞✅");
      if (ctx.loadAllTasks) ctx.loadAllTasks();
      setShowBugModal(false);
      setSelectedFile(null);
      setBugForm({ title: "", description: "", steps: "", severity: "Normal", version: latestBeta, tagIds: [] });
    } catch(e) {
      notify((isEN ? "Error: " : "Fehler: ") + e.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteBeta = async (betaId) => {
    if (!window.confirm("Bist du sicher, dass du diese Beta-Version dauerhaft löschen willst?")) return;
    try {
      await api(`/nl2/beta/${betaId}`, { method: "DELETE" });
      notify("Beta gelöscht!");
      if (ctx.loadNL2) ctx.loadNL2();
    } catch(e) {
      notify("Fehler beim Löschen: " + e.message);
    }
  };

  // 🚀 FIX: Zieht die Links jetzt korrekt aus beta.files
  const startEditingBeta = (beta) => {
    let parsedLinks = [];
    if (beta.files && Array.isArray(beta.files) && beta.files.length > 0) {
      parsedLinks = beta.files.map(f => ({ label: f.label, url: f.url }));
    } else {
      try {
        if (typeof beta.download_links === "string" && beta.download_links.startsWith("[")) {
          parsedLinks = JSON.parse(beta.download_links);
        }
      } catch(e) {}
    }
    
    if (parsedLinks.length === 0) parsedLinks = [{ label: "Download", url: "" }];

    setEditingBeta({ ...beta });
    setEditBetaLinks(parsedLinks);
  };

  const saveBetaEdit = async () => {
    setIsSavingBeta(true);
    try {
      const validLinks = editBetaLinks.filter(l => l.url.trim() !== "");
      
      await api(`/nl2/beta/${editingBeta.id}`, {
        method: "PUT",
        body: JSON.stringify({
          version: editingBeta.version,
          changelog: editingBeta.changelog || "",
          download_links: JSON.stringify(validLinks)
        })
      });
      
      notify("Änderungen gespeichert!");
      if (ctx.loadNL2) ctx.loadNL2();
      setEditingBeta(null);
    } catch(e) {
      notify("Fehler beim Speichern: " + e.message);
    } finally {
      setIsSavingBeta(false);
    }
  };

  const handleLegacyDownload = async (betaId, originalName) => {
    try {
      notify(isEN ? "Starting download..." : "Download startet...");
      const res = await apiFetch(`/nl2/beta/${betaId}/download`, { method: "GET" }, token);
      if (!res.ok) throw new Error("Download Error");
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = originalName || `Beta_Release.nl2pkg`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (e) {
      const fallbackUrl = ctx.api?.baseUrl ? `${ctx.api.baseUrl}/nl2/beta/${betaId}/download?token=${token}` : `/api/nl2/beta/${betaId}/download?token=${token}`;
      window.open(fallbackUrl, "_blank");
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* HEADER */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, borderBottom: `1px solid ${T.border}`, paddingBottom: 16, flexWrap: "wrap" }}>
        <div style={{ display: "flex", background: T.input, border: `1px solid ${T.border2}`, borderRadius: 11, padding: 3, gap: 3 }}>
          <button onClick={() => setActiveTab("bugs")} style={{ padding: "8px 16px", borderRadius: 9, border: "none", background: activeTab === "bugs" ? "#EF4444" : "transparent", color: activeTab === "bugs" ? "#fff" : T.textMuted, fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
            <Bug size={14} /> {isEN ? "Bug Tracker" : "Bug-Tracker"}
          </button>
          <button onClick={() => setActiveTab("releases")} style={{ padding: "8px 16px", borderRadius: 9, border: "none", background: activeTab === "releases" ? "#8B5CF6" : "transparent", color: activeTab === "releases" ? "#fff" : T.textMuted, fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
            <List size={14} /> Releases
          </button>
          <button onClick={() => setActiveTab("stats")} style={{ padding: "8px 16px", borderRadius: 9, border: "none", background: activeTab === "stats" ? "#3B82F6" : "transparent", color: activeTab === "stats" ? "#fff" : T.textMuted, fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
            <BarChart2 size={14} /> {isEN ? "Statistics" : "Statistiken"}
          </button>
        </div>

        {activeTab === "bugs" && (
          <div style={{ display: "flex", background: T.input, border: `1px solid ${T.border2}`, borderRadius: 11, padding: 3, gap: 3, marginLeft: 10 }}>
            <div onClick={() => setViewMode("list")} style={{ padding: "6px 12px", borderRadius: 8, cursor: "pointer", background: viewMode === "list" ? T.cardHover : "transparent", color: viewMode === "list" ? T.text : T.textMuted }}><List size={16} /></div>
            <div onClick={() => setViewMode("board")} style={{ padding: "6px 12px", borderRadius: 8, cursor: "pointer", background: viewMode === "board" ? T.cardHover : "transparent", color: viewMode === "board" ? T.text : T.textMuted }}><Kanban size={16} /></div>
          </div>
        )}

        <div style={{ flex: 1 }} />

        {isBetaAdmin && activeTab === "bugs" && (
          <div style={{ display: "flex", gap: 8, marginRight: 8 }}>
            <button onClick={() => { ctx.setActiveProjectId(betaProject.id); ctx.openAutomations(); }} style={{ background: T.cardHover, border: `1px solid ${T.border2}`, color: T.textMuted, padding: "0 14px", height: 38, borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
              <Zap size={14} /> Auto
            </button>
            <button onClick={() => { ctx.setActiveProjectId(betaProject.id); ctx.openTags(); }} style={{ background: T.cardHover, border: `1px solid ${T.border2}`, color: T.textMuted, padding: "0 14px", height: 38, borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
              <Tags size={14} /> Tags
            </button>
          </div>
        )}

        <button onClick={() => setShowBugModal(true)} style={{ background: "#EF4444", color: "#fff", border: "none", borderRadius: 10, height: 38, padding: "0 18px", fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, boxShadow: "0 4px 12px rgba(239, 68, 68, 0.2)" }}>
          <Plus size={16} /> {isEN ? "Report Bug" : "Bug eintragen"}
        </button>
      </div>

      {activeTab === "bugs" && (
        <>
          <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder={isEN ? "Search bugs..." : "Bugs durchsuchen..."} style={{ ...iS, width: 200, padding: "8px 14px", height: 36 }} />
            
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...iS, width: "auto", height: 36, padding: "0 14px" }}>
              <option value="Alle">{isEN ? "All Statuses" : "Alle Status"}</option>
              {(betaProject.customStatuses?.map(s => s.label) || DEFAULT_STATUSES).map(s => <option key={s} value={s}>{s}</option>)}
            </select>

            {/* 🚀 NEU: Das Mitarbeiter-Dropdown */}
            <select value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)} style={{ ...iS, width: "auto", height: 36, padding: "0 14px", cursor: "pointer" }}>
              <option value="Alle">{isEN ? "All Users" : "Alle Mitarbeiter"}</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>

            <select value={filterTag} onChange={e => setFilterTag(e.target.value)} style={{ ...iS, width: "auto", height: 36, padding: "0 14px" }}>
              <option value="">{isEN ? "All Tags & Versions" : "Alle Tags & Versionen"}</option>
              {(betaProject.tags || []).map(tg => <option key={tg.id} value={tg.id}>{tg.name}</option>)}
            </select>
            
            {/* Reset-Button erweitert um filterAssignee */}
            {(filterStatus !== "Alle" || filterTag !== "" || filterAssignee !== "Alle" || searchQuery !== "") && (
              <button onClick={() => { setFilterStatus("Alle"); setFilterTag(""); setFilterAssignee("Alle"); setSearchQuery(""); }} style={{ background: T.cardHover, border: `1px solid ${T.border2}`, color: T.textMuted, padding: "0 14px", height: 36, borderRadius: 9, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{isEN ? "Reset" : "Zurücksetzen"}</button>
            )}
          </div>
          <TaskBoard ctx={portalCtx} filteredTasks={filteredBetaTasks} tasksByStatus={tasksByStatus} projectView={viewMode} setProjectView={setViewMode} markChatRead={()=>{}} />
        </>
      )}

      {activeTab === "releases" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {(!nl2Betas || nl2Betas.length === 0) ? (
            <div style={{ padding: 60, textAlign: "center", color: T.textFaint, background: T.card, border: `1px solid ${T.border}`, borderRadius: 16 }}>
              <Clock size={48} style={{ opacity: 0.5, margin: "0 auto 16px" }} />
              <div>{isEN ? "No beta releases available yet." : "Noch keine Beta-Releases vorhanden."}</div>
            </div>
          ) : (
            nl2Betas.map(beta => {
              const versionBugs = betaTasks.filter(t => (t.tags || []).some(tg => tg.name.includes(beta.version)));
              const fixedCount = versionBugs.filter(t => t.status.toLowerCase().includes("fix") || t.status.toLowerCase().includes("erledigt") || t.status === (betaProject.customStatuses?.[betaProject.customStatuses.length-1]?.label)).length;

              let links = [];
              let isLegacyFile = false;

              // 🚀 FIX: Zieht die Links jetzt korrekt aus beta.files
              if (beta.files && Array.isArray(beta.files) && beta.files.length > 0) {
                links = beta.files.map(f => ({ label: f.label, url: f.url }));
              } else {
                try { 
                  if (typeof beta.download_links === "string" && beta.download_links.trim() !== "") {
                    if (beta.download_links.trim().startsWith("[")) {
                      links = JSON.parse(beta.download_links); 
                    } else {
                      links = [{ label: "Download", url: beta.download_links }];
                    }
                  }
                } catch(e) {}
              }
              
              if (links.length === 0 && (beta.filename || beta.original_name)) {
                isLegacyFile = true;
                links = [{ 
                  label: beta.original_name ? `Download: ${beta.original_name}` : "Download Legacy Beta", 
                  isLegacy: true, 
                  betaId: beta.id, 
                  originalName: beta.original_name || beta.filename 
                }];
              }

              const cleanVersion = beta.version?.replace(/^V+/i, '') || "Unknown";

              return (
                <div key={beta.id} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, overflow: "hidden" }}>
                  <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, background: "rgba(0,0,0,0.15)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: T.text, display: "flex", alignItems: "center", gap: 8 }}>
                      <Rocket size={18} color="#8B5CF6" /> Version {cleanVersion}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ fontSize: 12, color: T.textFaint }}>{fmtDate(beta.created_at)}</div>
                      
                      {isBetaAdmin && (
                        <div style={{ display: "flex", alignItems: "center", gap: 6, borderLeft: `1px solid ${T.border2}`, paddingLeft: 12 }}>
                          <button onClick={() => startEditingBeta(beta)} style={{ background: "transparent", border: "none", color: T.textMuted, cursor: "pointer", padding: 4 }} title="Bearbeiten"><PenTool size={16} /></button>
                          <button onClick={() => deleteBeta(beta.id)} style={{ background: "transparent", border: "none", color: "#EF4444", cursor: "pointer", padding: 4 }} title="Löschen"><Trash2 size={16} /></button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ padding: "20px" }}>
                    <div style={{ fontSize: 13, color: T.textMuted, whiteSpace: "pre-wrap", lineHeight: 1.6, marginBottom: 16 }}>
                      {beta.changelog || (isEN ? "No changelog provided." : "Kein Changelog hinterlegt.")}
		      {beta.log_summary && (
                       <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px dashed ${T.border2}`, color: T.textSub }}>
                       {beta.log_summary}
                       </div>
                       )}
                    </div>
                    
                    {links.length > 0 && (
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
                        {links.map((link, i) => {
                          if (link.isLegacy) {
                            return (
                              <button key={i} onClick={() => handleLegacyDownload(link.betaId, link.originalName)} style={{ display: "flex", alignItems: "center", gap: 6, background: "#8B5CF6", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 9, fontSize: 12, fontWeight: 600, cursor: "pointer", boxShadow: "0 4px 12px rgba(139, 92, 246, 0.2)", transition: "all 0.2s" }}>
                                <Download size={14} /> {link.label}
                              </button>
                            );
                          }
                          return (
                            <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" style={{ display: "flex", alignItems: "center", gap: 6, background: "#8B5CF6", color: "#fff", padding: "8px 16px", borderRadius: 9, fontSize: 12, fontWeight: 600, textDecoration: "none", boxShadow: "0 4px 12px rgba(139, 92, 246, 0.2)", transition: "all 0.2s" }}>
                              <Download size={14} /> {link.label || "Download"}
                            </a>
                          );
                        })}
                      </div>
                    )}
                    
                    <div style={{ display: "flex", gap: 12, alignItems: "center", background: T.cardHover, border: `1px solid ${T.border2}`, borderRadius: 10, padding: 12 }}>
                      <Bug size={16} color={T.textFaint} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: T.textFaint, letterSpacing: "0.05em", marginBottom: 2 }}>{isEN ? "BUG REPORTING FOR THIS VERSION" : "BUG REPORTING FÜR DIESE VERSION"}</div>
                        <div style={{ fontSize: 13, color: T.textSub }}>{versionBugs.length} {isEN ? "reported" : "gemeldet"}</div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#34D399", fontWeight: 600, background: "#34D39915", padding: "6px 12px", borderRadius: 8 }}>
                        <CheckCircle size={16} /> {fixedCount} {isEN ? "resolved" : "behoben"}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* TAB: STATISTIKEN */}
      {activeTab === "stats" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {(() => {
             const total = betaTasks.length;
             const critical = betaTasks.filter(t => t.priority === 0).length;
             const fixed = betaTasks.filter(t => t.status.toLowerCase().includes("fix") || t.status.toLowerCase().includes("erledigt") || t.status === (betaProject.customStatuses?.[betaProject.customStatuses.length-1]?.label)).length;
             
             return (
               <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14 }}>
                 <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: "20px" }}>
                   <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}><Bug size={18} color="#EF4444" /><span style={{ fontSize: 11, color: T.textGhost, fontWeight: 700, letterSpacing: "0.1em" }}>{isEN ? "REPORTED BUGS" : "GEMELDETE BUGS"}</span></div>
                   <div style={{ fontSize: 32, fontWeight: 700, color: "#EF4444", fontFamily: "'DM Mono', monospace" }}>{total}</div>
                 </div>
                 <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: "20px" }}>
                   <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}><CheckCircle size={18} color="#34D399" /><span style={{ fontSize: 11, color: T.textGhost, fontWeight: 700, letterSpacing: "0.1em" }}>{isEN ? "RESOLVED (FIXED)" : "BEHOBEN (FIXED)"}</span></div>
                   <div style={{ fontSize: 32, fontWeight: 700, color: "#34D399", fontFamily: "'DM Mono', monospace" }}>{fixed}</div>
                 </div>
                 <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: "20px" }}>
                   <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}><AlertTriangle size={18} color="#F59E0B" /><span style={{ fontSize: 11, color: T.textGhost, fontWeight: 700, letterSpacing: "0.1em" }}>{isEN ? "CRITICAL CRASHES" : "KRITISCHE CRASHES"}</span></div>
                   <div style={{ fontSize: 32, fontWeight: 700, color: "#F59E0B", fontFamily: "'DM Mono', monospace" }}>{critical}</div>
                 </div>
               </div>
             );
          })()}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16 }}>
            <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: "24px" }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 18 }}>{isEN ? "Status Distribution" : "Verteilung nach Status"}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {Object.keys(tasksByStatus).map(status => {
                   const count = tasksByStatus[status].length;
                   const max = Math.max(...Object.values(tasksByStatus).map(arr => arr.length)) || 1;
                   const pct = max > 0 ? Math.round((count / max) * 100) : 0;
                   const meta = portalCtx.getStatusMeta(status);
                   return (
                     <div key={status} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                       <span style={{ fontSize: 12, color: T.textSub, width: 130, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{status}</span>
                       <div style={{ flex: 1, background: T.cardHover, borderRadius: 4, height: 8 }}>
                         <div style={{ width: `${pct}%`, height: "100%", background: meta.color, borderRadius: 4, transition: "width 0.5s ease" }} />
                       </div>
                       <span style={{ fontSize: 12, color: T.textFaint, width: 30, textAlign: "right", fontFamily: "'DM Mono', monospace" }}>{count}</span>
                     </div>
                   );
                })}
              </div>
            </div>
            
            <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: "24px" }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 18, display: "flex", alignItems: "center", gap: 8 }}>
                <Tags size={18} color="#EC4899" /> {isEN ? "Most Frequent Categories" : "Häufigste Kategorien"}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {tagStats.length === 0 && <div style={{ fontSize: 13, color: T.textFaint }}>{isEN ? "No tags assigned." : "Keine Tags vergeben."}</div>}
                {tagStats.slice(0, 8).map((ts) => {
                  const max = tagStats[0]?.count || 1;
                  const pct = Math.round((ts.count / max) * 100);
                  return (
                    <div key={ts.name} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 12, color: ts.color, width: 130, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 600 }}>{ts.name}</span>
                      <div style={{ flex: 1, background: T.cardHover, borderRadius: 4, height: 8 }}>
                         <div style={{ width: `${pct}%`, height: "100%", background: ts.color, borderRadius: 4, transition: "width 0.5s ease" }} />
                      </div>
                      <span style={{ fontSize: 12, color: T.textFaint, width: 30, textAlign: "right", fontFamily: "'DM Mono', monospace" }}>{ts.count}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 16, padding: "24px" }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 18, display: "flex", alignItems: "center", gap: 8 }}>
                <Award size={18} color="#3B82F6" /> {isEN ? "Top Bug Hunters" : "Top Bug-Jäger"}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {topReporters.length === 0 && <div style={{ fontSize: 13, color: T.textFaint }}>{isEN ? "No bugs reported yet." : "Noch keine Bugs gemeldet."}</div>}
                {topReporters.slice(0, 8).map(([name, count], index) => {
                  const max = topReporters[0]?.[1] || 1;
                  const pct = Math.round((count / max) * 100);
                  const colorIndex = name.length % AVATAR_COLORS.length;
                  const color = AVATAR_COLORS[colorIndex];
                  
                  return (
                    <div key={name} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ width: 20, fontSize: 12, fontWeight: 700, color: T.textGhost, textAlign: "right" }}>#{index + 1}</div>
                      <div style={{ width: 28, height: 28, borderRadius: 8, background: color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#0A0A0F" }}>
                        {initials(name)}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.textSub, marginBottom: 4 }}>{name}</div>
                        <div style={{ background: T.cardHover, borderRadius: 4, height: 6 }}>
                           <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 4, transition: "width 0.5s ease" }} />
                        </div>
                      </div>
                      <div style={{ fontSize: 13, color: T.textFaint, fontWeight: 700, fontFamily: "'DM Mono', monospace" }}>{count}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: BETA BEARBEITEN (WARTUNGSMODUS) */}
      {editingBeta && (
         <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", backdropFilter: "blur(10px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
             <div style={{ background: T.sidebar, border: `1px solid ${T.border3}`, borderRadius: 22, padding: "32px", width: 550, boxShadow: "0 32px 72px rgba(0,0,0,0.6)" }}>
               <h2 style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 20, display: "flex", alignItems: "center", gap: 8 }}><PenTool color="#8B5CF6" /> Beta V{editingBeta.version} bearbeiten</h2>
               
               <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                 <Field label="VERSION" T={T}>
                    <input value={editingBeta.version} onChange={e => setEditingBeta({...editingBeta, version: e.target.value})} style={iS} />
                 </Field>
                 <Field label="CHANGELOG" T={T}>
                    <textarea value={editingBeta.changelog || ""} onChange={e => setEditingBeta({...editingBeta, changelog: e.target.value})} rows={3} style={{...iS, resize:"vertical"}} />
                 </Field>
                 
                 <Field label="DOWNLOAD LINKS" T={T}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {editBetaLinks.map((link, idx) => (
                        <div key={idx} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <input value={link.label} onChange={e => { const n = [...editBetaLinks]; n[idx].label = e.target.value; setEditBetaLinks(n); }} placeholder="Label (z.B. Google Drive)" style={{ ...iS, width: 140 }} />
                          <input value={link.url} onChange={e => { const n = [...editBetaLinks]; n[idx].url = e.target.value; setEditBetaLinks(n); }} placeholder="https://..." style={{ ...iS, flex: 1 }} />
                          {editBetaLinks.length > 1 && (
                            <button onClick={() => setEditBetaLinks(editBetaLinks.filter((_, i) => i !== idx))} style={{ background: "transparent", border: "none", color: "#FF4757", cursor: "pointer", padding: "0 8px" }}><Trash2 size={16} /></button>
                          )}
                        </div>
                      ))}
                      <button onClick={() => setEditBetaLinks([...editBetaLinks, {label: "Weiterer Link", url: ""}])} style={{ background: "transparent", border: `1px dashed ${T.border3}`, color: T.textMuted, padding: "8px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>+ Link hinzufügen</button>
                    </div>
                 </Field>
               </div>
               
               <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 24 }}>
                 <button onClick={() => setEditingBeta(null)} style={{ background: "transparent", border: `1px solid ${T.border2}`, color: T.textMuted, padding: "9px 18px", borderRadius: 9, cursor: "pointer", fontWeight: 600 }}>Abbrechen</button>
                 <button onClick={saveBetaEdit} disabled={isSavingBeta} style={{ background: "#8B5CF6", border: "none", color: "#fff", padding: "9px 22px", borderRadius: 9, fontWeight: 700, cursor: "pointer", opacity: isSavingBeta ? 0.5 : 1 }}>
                    {isSavingBeta ? "Speichern..." : "Speichern"}
                 </button>
               </div>
             </div>
           </div>
      )}

      {/* MODAL: NEUER BUG */}
      {showBugModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", backdropFilter: "blur(10px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
             <div style={{ background: T.sidebar, border: `1px solid ${T.border3}`, borderRadius: 22, padding: "32px", width: 550, boxShadow: "0 32px 72px rgba(0,0,0,0.6)" }}>
               <h2 style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 20, display: "flex", alignItems: "center", gap: 8 }}><AlertTriangle color="#EF4444" /> {isEN ? "Report New Bug" : "Neuen Bug melden"}</h2>
               
               <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                 <Field label={isEN ? "TITLE" : "TITEL"} T={T}><input value={bugForm.title} onChange={e => setBugForm({ ...bugForm, title: e.target.value })} style={iS} autoFocus placeholder={isEN ? "e.g. Texture flickering in station" : "z.B. Textur flackert in Station"} /></Field>
                 
                 <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                   <Field label="VERSION" T={T}>
                     <select value={bugForm.version} onChange={e => setBugForm({ ...bugForm, version: e.target.value })} style={iS}>
                       {(nl2Betas || []).map(b => <option key={b.id} value={b.version}>v{b.version}</option>)}
                       <option value="Unbekannt">{isEN ? "Unknown" : "Unbekannt"}</option>
                     </select>
                   </Field>
                   <Field label={isEN ? "SEVERITY" : "SCHWEREGRAD"} T={T}>
                     <select value={bugForm.severity} onChange={e => setBugForm({ ...bugForm, severity: e.target.value })} style={iS}>
                       <option value="Kosmetisch">{isEN ? "Cosmetic (Graphic/Text)" : "Kosmetisch (Grafik/Text)"}</option>
                       <option value="Normal">{isEN ? "Normal Bug" : "Normaler Bug"}</option>
                       <option value="Kritisch">{isEN ? "Critical (Crash)" : "Kritisch (Crash)"}</option>
                     </select>
                   </Field>
                 </div>

                 <Field label={isEN ? "CATEGORIES (TAGS)" : "KATEGORIEN (TAGS)"} T={T}>
                   <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                     {(betaProject.tags || []).filter(tg => !tg.name.startsWith("V")).map(tg => {
                       const isSel = bugForm.tagIds.includes(tg.id);
                       return (
                         <div key={tg.id} onClick={() => setBugForm({ ...bugForm, tagIds: isSel ? bugForm.tagIds.filter(id => id !== tg.id) : [...bugForm.tagIds, tg.id] })} style={{ padding: "5px 12px", borderRadius: 8, cursor: "pointer", background: isSel ? tg.color + "30" : T.input, border: `1px solid ${isSel ? tg.color : T.border2}`, color: isSel ? tg.color : T.textMuted, fontSize: 11, fontWeight: 600, transition: "all 0.15s" }}>
                           {tg.name}
                         </div>
                       );
                     })}
                   </div>
                 </Field>
                 
                 <div style={{display: "flex", flexDirection: "column", gap: 12}}>
            <div>
              <div style={{fontSize: 10, color: T.textFaint, fontWeight: 700, letterSpacing: "0.12em", marginBottom: 4}}>
                {isEN ? "DESCRIPTION & REPRODUCTION" : "BESCHREIBUNG & REPRODUKTION"}
              </div>
              <div style={{fontSize: 10, color: T.textGhost, marginBottom: 8}}>
                {isEN ? "Supports **Bold**, *Italic*, `Code` and [Link](url)" : "Unterstützt **Fett**, *Kursiv*, `Code` und [Link](url)"}
              </div>
              <textarea value={bugForm.description} onChange={e=>setBugForm(f=>({...f,description:e.target.value}))} placeholder={isEN ? "What happened?" : "Was ist passiert?"} style={{...iS, height: 80, resize: "vertical", marginBottom: 12}} />
              <textarea value={bugForm.steps} onChange={e=>setBugForm(f=>({...f,steps:e.target.value}))} placeholder={isEN ? "Steps to reproduce..." : "Schritte zur Reproduktion..."} style={{...iS, height: 80, resize: "vertical"}} />
            </div>
          </div>
                 <Field label={isEN ? "SCREENSHOT / FILE" : "SCREENSHOT / DATEI"} T={T}>
                    {!selectedFile ? (
                      <div onClick={() => fileInputRef.current.click()} style={{ border: `2px dashed ${T.border3}`, borderRadius: 12, padding: 12, textAlign: "center", cursor: "pointer", color: T.textMuted, fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                        <Paperclip size={14} /> {isEN ? "Attach file (Max 200MB)" : "Datei anhängen (Max. 200MB)"}
                        <input type="file" ref={fileInputRef} style={{ display: "none" }} onChange={e => setSelectedFile(e.target.files[0])} />
                      </div>
                    ) : (
                      <div style={{ background: T.cardHover, padding: "8px 12px", borderRadius: 10, display: "flex", alignItems: "center", gap: 10, border: `1px solid #34D39940` }}>
                        <ImageIcon size={16} color="#34D399" />
                        <span style={{ flex: 1, fontSize: 12, color: T.textSub, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selectedFile.name}</span>
                        <div onClick={() => setSelectedFile(null)} style={{ cursor: "pointer", color: "#FF4757" }}><X size={16} /></div>
                      </div>
                    )}
                 </Field>
               </div>
               
               <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 24 }}>
                 <button onClick={() => setShowBugModal(false)} style={{ background: "transparent", border: `1px solid ${T.border2}`, color: T.textMuted, padding: "9px 18px", borderRadius: 9, cursor: "pointer", fontWeight: 600 }}>{isEN ? "Cancel" : "Abbrechen"}</button>
                 <button onClick={submitBug} disabled={issubmitting} style={{ background: "#EF4444", border: "none", color: "#fff", padding: "9px 22px", borderRadius: 9, fontWeight: 700, cursor: "pointer", opacity: issubmitting ? 0.5 : 1 }}>
                    {issubmitting ? (isEN ? "Submitting..." : "Wird gesendet...") : (isEN ? "Submit Bug" : "Bug einreichen")}
                 </button>
               </div>
             </div>
           </div>
      )}
    </div>
  );
}